import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "../../utils/axios";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import type {
  IFilterData,
  IHeader,
  IQuickFilter,
} from "../../components/Organismos/Table/FilteredTable.types";
import type { ISorting } from "../../interfaces/Components/ITable";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import ShapeEdit from "../../components/Atomos/Icons/ShapeEdit";
import Modal from "../../components/Atomos/Modals/Modal";
import Close from "../../components/Atomos/Icons/Close";
import SaveChanges from "../../components/Atomos/Icons/EditMode/SaveChanges";
import { UsePracticeIndustryList } from "../../hooks/Catalogs/usePracticeIndustry";
import { UseFirmList, firmType } from "../../hooks/Catalogs/useFirm";
import { getNameById } from "../../utils/functions";
import { toastError, toastSuccess } from "../../utils/toast";
import Button from "../../components/staff/Button";
import CheckBox from "../../components/staff/CheckBox";
import Plus from "../../components/Atomos/Icons/EditMode/Plus";
import { AOPMappingTruncatedListCell } from "./AOPMappingTruncatedListCell";
import "./StaffAopCheckboxes.css";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import QuestionCircle from "../../components/Atomos/Icons/QuestionCircle";

const AOP_MAPPING_PAGE_SIZE = 50;
const AOP_MAPPING_ENDPOINT = "/AreaOfPractice";
const AOP_MAPPING_DEFAULT_SORT: ISorting = {
  field: "displayName",
  order: "asc",
};
const AOP_MAPPING_QUICK_FILTERS: IQuickFilter[] = [
  {
    key: "unmapped",
    label: "Unmapped",
    hiddenQuery: {
      practiceIndustryIds: {
        value: true,
        comparisonType: "EMPTY_OR_NOT_DEFINED",
      },
      isIgnored: { value: true, comparisonType: "NOT_EQUAL_OR_NULL" },
    },
  },
  {
    key: "mapped",
    label: "Mapped",
    hiddenQuery: {
      practiceIndustryIds: { value: [], comparisonType: "IS_DEFINED" },
      isIgnored: { value: true, comparisonType: "NOT_EQUAL_OR_NULL" },
    },
  },
  {
    key: "ignored",
    label: "Ignored",
    hiddenQuery: {
      isIgnored: { value: true, comparisonType: "EQUAL" },
    },
  },
];

/** Match {@link AOPMappingForm} footer: equal width at sm+. */
const BULK_MODAL_FOOTER_BUTTON_CLASS =
  "w-full justify-center sm:w-[12.5rem] md:w-[12.5rem] lg:w-[12.5rem] shrink-0";

const FIRMS_PREVIEW_COUNT = 6;

type BulkIndustryRow = { id: string; name: string; isChecked?: boolean };

type BulkAopRow = { id: string; displayName: string };

function uniqueSortedFirmNamesForAopIds(
  aopIds: string[],
  firms: { aopIds?: string[]; name?: string; firmName?: string }[],
): string[] {
  const idSet = new Set(aopIds.map(String));
  const names = new Set<string>();
  for (const f of firms) {
    const aops = f?.aopIds;
    if (!Array.isArray(aops) || !aops.some((x) => idSet.has(String(x)))) {
      continue;
    }
    const n = f.name ?? f.firmName;
    if (n) {
      names.add(String(n).trim());
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Per-AOP help icon: hover shows all firms that reference this AOP (from firm `aopIds`). */
const AopFirmsTooltip: React.FC<{
  firmNames: string[];
  labelForAop: string;
}> = ({ firmNames, labelForAop }) => {
  const { isStaffMode } = useStaffMode();
  const ariaFirms =
    firmNames.length === 0
      ? `No firms are linked to ${labelForAop} in the catalog`
      : `Firms for ${labelForAop}: ${firmNames.join(", ")}`;
  return (
    <div className="group relative inline-flex shrink-0">
      <span
        className={`inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full p-1 ${
          isStaffMode ? "bg-secondary-dark" : "bg-primary-dark"
        } text-white`}
        role="img"
        aria-label={ariaFirms}
      >
        <QuestionCircle />
      </span>
      {/** Opens below the icon (not above) so the panel stays inside the modal’s scrollport and is not clipped. */}
      <div className="absolute left-1/2 top-full z-50 mt-1.5 hidden min-w-[12.5rem] max-w-sm -translate-x-1/2 group-hover:block">
        <div className="relative w-max min-w-[12.5rem] max-w-sm overflow-hidden rounded border border-white/10 bg-gray-font text-sm text-neutral shadow-lg">
          <div
            className="pointer-events-none absolute bottom-full left-1/2 mb-px h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-font"
            aria-hidden
          />
          <div className="max-h-60 overflow-y-auto overflow-x-hidden py-2 pl-3 pr-2 text-left [scrollbar-gutter:stable]">
            {firmNames.length === 0 ? (
              <p className="m-0 leading-snug">No firms are linked to this AOP</p>
            ) : (
              <ul className="m-0 list-none space-y-1.5 p-0">
                {firmNames.map((n) => (
                  <li key={n} className="break-words leading-snug">
                    {n}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AOPMapping: React.FC = () => {
  const navigate = useNavigate();
  const { practiceIndustryData: practiceIndustries } = UsePracticeIndustryList();
  const { firmData, refetchFirms } = UseFirmList(firmType.both);

  useEffect(() => {
    void refetchFirms();
  }, [refetchFirms]);

  const axios = useMemo(() => new Axios(), []);

  /** Live quick-filter key from FilteredTableCore (table prefs are debounced ~1s and would lag the chips). */
  const [quickFilterKey, setQuickFilterKey] = useState<string>("unmapped");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkOpenLoading, setBulkOpenLoading] = useState(false);
  const [bulkAopRows, setBulkAopRows] = useState<BulkAopRow[]>([]);
  const [bulkIndustryRows, setBulkIndustryRows] = useState<BulkIndustryRow[]>([]);
  const [firmsInModalExpanded, setFirmsInModalExpanded] = useState(false);
  const [tableRemountKey, setTableRemountKey] = useState(0);

  const isMappedTab = quickFilterKey === "mapped";

  const onQuickFilterChange = useCallback((key: string) => {
    setQuickFilterKey(key);
    setSelectedIds(new Set());
    setMapModalOpen(false);
    setFirmsInModalExpanded(false);
    setBulkAopRows([]);
    setBulkIndustryRows([]);
  }, []);

  const filtersData: IFilterData[] = useMemo(() => {
    const firms = Array.isArray(firmData) ? firmData : [];
    const industries = Array.isArray(practiceIndustries) ? practiceIndustries : [];
    return [
      {
        name: "Firm",
        fieldName: "firms",
        comparison: "ARRAY_CONTAINS",
        objectData: firms.map((f: { id: string; name: string }) => ({
          id: f.id,
          name: f.name,
        })),
      },
      {
        name: "Practice Industry",
        fieldName: "practiceIndustries",
        comparison: "ARRAY_CONTAINS",
        objectData: industries.map((p: { id: string; name: string }) => ({
          id: p.id,
          name: p.name,
        })),
        disabled: !isMappedTab,
      },
    ];
  }, [firmData, practiceIndustries, isMappedTab]);

  const rowSelection = useMemo(
    () =>
      isMappedTab
        ? undefined
        : {
            selectedIds,
            onToggleRow: (id: string, selected: boolean) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (selected) {
                  next.add(id);
                }
                else {
                  next.delete(id);
                }
                return next;
              });
            },
            onTogglePage: (pageRowIds: string[], selectAll: boolean) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (selectAll) {
                  pageRowIds.forEach((id) => next.add(id));
                }
                else {
                  pageRowIds.forEach((id) => next.delete(id));
                }
                return next;
              });
            },
            getRowId: (row: any) => String(row?.id ?? ""),
          },
    [isMappedTab, selectedIds],
  );

  const formatData = useCallback(
    (rows: any[]) => {
      if (!rows?.length) {
        return [];
      }
      const catalog = Array.isArray(practiceIndustries) ? practiceIndustries : [];
      const firms = Array.isArray(firmData) ? firmData : [];

      const firmNamesForAop = (aopId: string): string => {
        const idStr = String(aopId);
        const names: string[] = [];
        for (const f of firms) {
          const ids = f?.aopIds;
          if (!Array.isArray(ids) || !ids.map(String).includes(idStr)) {
            continue;
          }
          const n = f.name ?? f.firmName;
          if (n) {
            names.push(String(n).trim());
          }
        }
        if (names.length === 0) {
          return "—";
        }
        return [...new Set(names)].sort((a, b) => a.localeCompare(b)).join(", ");
      };

      const statusLabel = (r: any, piIds: unknown[]) => {
        if (r.isIgnored === true) {
          return "Ignored";
        }
        if (Array.isArray(piIds) && piIds.length > 0) {
          return "Mapped";
        }
        return "Unmapped";
      };

      return rows.map((r) => {
        const id = String(r?.id ?? "");
        const ids = r.practiceIndustryIds || r.practiceIndustries || [];
        const resolved =
          Array.isArray(ids) && ids.length > 0 ? getNameById(ids, catalog).join(", ") : "";
        return {
          ...r,
          practiceIndustryNames: r.practiceIndustryNames || resolved || "Not mapped",
          firmNames: firmNamesForAop(id),
          statusLabel: statusLabel(r, ids),
        };
      });
    },
    [practiceIndustries, firmData],
  );

  const columnsDef = useMemo<IHeader[]>(
    () => [
      {
        id: "edit",
        header: <span className="sr-only">Edit</span>,
        width: 44,
        enableSorting: false,
        columnOrderFixed: true,
        cell: ({ row }: { row: any }) => (
          <span className="inline-flex items-center justify-center" title="Edit">
            <PersistentLink
              className="inline-flex items-center justify-center p-0.5 text-current hover:opacity-80"
              to={`/AOP/edit/${row.id}`}
              state={row}
            >
              <span className="sr-only">Edit</span>
              <div className="editsvg !h-4 !w-4 shrink-0">
                <ShapeEdit />
              </div>
            </PersistentLink>
          </span>
        ),
      },
      { id: "displayName", header: "AOP Name", width: 240 },
      {
        id: "firmNames",
        header: "Firms",
        width: 220,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <AOPMappingTruncatedListCell text={row.firmNames ?? "—"} />
        ),
      },
      {
        id: "practiceIndustryNames",
        header: "Practice Industries",
        width: 260,
        cell: ({ row }: { row: any }) => (
          <AOPMappingTruncatedListCell
            text={row.practiceIndustryNames || "Not mapped"}
          />
        ),
      },
      {
        id: "professionals",
        header: "# Professionals",
        width: 120,
        enableSorting: false,
      },
      {
        id: "statusLabel",
        header: "Status",
        width: 110,
        enableSorting: false,
        cell: ({ row }: { row: any }) => <span>{row.statusLabel ?? ""}</span>,
      },
    ],
    [],
  );

  const firmsListInModal = useMemo(
    () =>
      uniqueSortedFirmNamesForAopIds(
        bulkAopRows.map((r) => r.id),
        Array.isArray(firmData) ? firmData : [],
      ),
    [bulkAopRows, firmData],
  );

  const firmNamesBySelectedAopId = useMemo(() => {
    const firms = Array.isArray(firmData) ? firmData : [];
    return new Map(
      bulkAopRows.map((r) => [r.id, uniqueSortedFirmNamesForAopIds([r.id], firms)]),
    );
  }, [bulkAopRows, firmData]);

  const firmsPreview = useMemo(() => {
    if (firmsInModalExpanded || firmsListInModal.length <= FIRMS_PREVIEW_COUNT) {
      return { visible: firmsListInModal, hasMore: false };
    }
    return {
      visible: firmsListInModal.slice(0, FIRMS_PREVIEW_COUNT),
      hasMore: true,
    };
  }, [firmsInModalExpanded, firmsListInModal]);

  const toggleAllBulkIndustries = useCallback(() => {
    setBulkIndustryRows((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const allOn = prev.every((i) => i.isChecked);
      return prev.map((i) => ({ ...i, isChecked: !allOn }));
    });
  }, []);

  const openBulkMappingModal = useCallback(async () => {
    if (isMappedTab || selectedIds.size === 0) {
      return;
    }
    setBulkOpenLoading(true);
    try {
      const ids = [...selectedIds];
      const filter = { id: { value: ids, comparisonType: "IN" } };
      const params = new URLSearchParams();
      params.append("filter", JSON.stringify(filter));
      params.append(
        "pagination",
        JSON.stringify({ pageIndex: 0, pageSize: Math.max(ids.length, 1) }),
      );
      const res = await axios.Get(`AreaOfPractice?${params.toString()}`);
      const rows: any[] = Array.isArray(res.data) ? res.data : [];
      const byId = new Map(rows.map((r) => [String(r.id), r]));
      const aopList: BulkAopRow[] = ids.map((id) => {
        const r = byId.get(String(id));
        const displayName = (String(r?.displayName ?? r?.name ?? id).trim() || id) as string;
        return { id, displayName };
      });
      setBulkAopRows(aopList);
      const pi = Array.isArray(practiceIndustries) ? practiceIndustries : [];
      setBulkIndustryRows(
        pi.map((p: { id: string; name: string }) => ({
          id: p.id,
          name: p.name,
          isChecked: false,
        })),
      );
      setFirmsInModalExpanded(false);
      setMapModalOpen(true);
    } catch (e: any) {
      toastError(e?.message ?? "Could not load selected AOPs.");
    } finally {
      setBulkOpenLoading(false);
    }
  }, [axios, isMappedTab, selectedIds, practiceIndustries]);

  const closeMapModal = useCallback(() => {
    if (bulkSaving) {
      return;
    }
    setMapModalOpen(false);
    setFirmsInModalExpanded(false);
    setBulkAopRows([]);
    setBulkIndustryRows([]);
  }, [bulkSaving]);

  const removeBulkAop = useCallback((id: string) => {
    setBulkAopRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const applyBulkMapping = useCallback(async () => {
    const aopCount = bulkAopRows.length;
    const selectedPi = bulkIndustryRows.filter((i) => i.isChecked);
    if (aopCount === 0 || selectedPi.length === 0) {
      return;
    }
    const labelList = selectedPi.map((i) => i.name).join(", ");
    const ok = window.confirm(
      `Apply mapping for ${aopCount} AOP(s) to the following practice ${
        selectedPi.length === 1 ? "industry" : "industries"
      }: ${labelList}?\n\nExisting industry links are kept; new ones are merged. Ignored items are un-ignored when saved.`,
    );
    if (!ok) {
      return;
    }
    setBulkSaving(true);
    try {
      const ids = bulkAopRows.map((r) => r.id);
      const filter = { id: { value: ids, comparisonType: "IN" } };
      const params = new URLSearchParams();
      params.append("filter", JSON.stringify(filter));
      params.append(
        "pagination",
        JSON.stringify({ pageIndex: 0, pageSize: Math.max(ids.length, 1) }),
      );
      const res = await axios.Get(`AreaOfPractice?${params.toString()}`);
      const rows: any[] = Array.isArray(res.data) ? res.data : [];
      const byId = new Map(rows.map((r) => [r.id, r]));
      const newPiIds = selectedPi.map((b) => b.id).filter(Boolean) as string[];
      for (const id of ids) {
        const current = byId.get(id);
        if (!current) {
          continue;
        }
        const mergedIds = [
          ...new Set([
            ...(Array.isArray(current.practiceIndustryIds) ? current.practiceIndustryIds : []),
            ...newPiIds,
          ]),
        ];
        await axios.Put("AreaOfPractice", {
          id: current.id,
          practiceIndustryIds: mergedIds,
          isIgnored: false,
        });
      }
      toastSuccess("Practice industries updated for the selected AOPs.");
      setMapModalOpen(false);
      setFirmsInModalExpanded(false);
      setBulkAopRows([]);
      setBulkIndustryRows([]);
      setSelectedIds(new Set());
      setTableRemountKey((k) => k + 1);
    } catch (e: any) {
      toastError(e?.message ?? "Could not update mappings.");
    } finally {
      setBulkSaving(false);
    }
  }, [axios, bulkAopRows, bulkIndustryRows]);

  const mapButtonDisabled = isMappedTab || selectedIds.size === 0;
  const canSaveBulk =
    bulkAopRows.length > 0 && bulkIndustryRows.some((i) => i.isChecked) && !bulkSaving;

  const mapModal = (
    <Modal
      isModalOpen={mapModalOpen}
      toggleModal={closeMapModal}
      title="Bulk Mapping"
      titleColor="text-secondary font-semibold !text-2xl !leading-8 sm:!text-[1.75rem] sm:!leading-8 md:!text-[2rem] md:!leading-9"
      compact
      outerPaddingOnly
      compactBodyScrolls={false}
      bodyClassName="!min-h-0 !flex-1 !shrink-1 !flex !flex-col !overflow-hidden"
      className="!max-w-6xl w-full p-4 sm:p-6 md:p-8 lg:p-10"
    >
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-0.5 overscroll-y-contain [scrollbar-gutter:stable] sm:space-y-5 sm:pr-2">
        <section>
          <h2 className="mb-3 text-lg font-semibold leading-snug text-gray-font sm:mb-4 md:text-xl">
            Firms
          </h2>
          <div className="rounded-md border border-gray-100 p-3 sm:p-4">
            <p className="m-0 text-base font-semibold leading-normal text-gray-font">Firm(s)</p>
            {firmsListInModal.length === 0 ? (
              <p className="mt-3 text-base text-gray-500">—</p>
            ) : (
              <>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-base leading-normal">
                  {firmsPreview.visible.map((name, i) => (
                    <span
                      key={`${name}-${i}`}
                      className="inline-flex max-w-full min-w-0 items-center gap-1 rounded bg-gray-100 px-2 py-1 text-base text-gray-font"
                    >
                      <span className="line-clamp-2 min-w-0 break-words" title={name}>
                        {name}
                      </span>
                    </span>
                  ))}
                </div>
                {firmsPreview.hasMore && (
                  <button
                    type="button"
                    className="mt-2 text-left text-base font-medium text-secondary underline hover:text-secondary-dark"
                    onClick={() => setFirmsInModalExpanded(true)}
                  >
                    View more
                  </button>
                )}
              </>
            )}
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-lg font-semibold leading-snug text-gray-font sm:mb-4 md:text-xl">
            Area of Practice
          </h2>
          <div className="rounded-md border border-gray-100 p-3 sm:p-4">
            <p className="m-0 text-base font-semibold leading-normal text-gray-font">AOP(s)</p>
            {bulkAopRows.length === 0 ? (
              <p className="mt-3 text-base text-gray-500">
                No AOPs in this batch. Use Go back to return.
              </p>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-base leading-normal">
                {bulkAopRows.map((row) => (
                  <span
                    key={row.id}
                    className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded bg-gray-100 px-2 py-1 text-base text-gray-font"
                  >
                    <span className="line-clamp-2 min-w-0 break-words" title={row.displayName}>
                      {row.displayName}
                    </span>
                    <AopFirmsTooltip
                      firmNames={firmNamesBySelectedAopId.get(row.id) ?? []}
                      labelForAop={row.displayName}
                    />
                    <button
                      type="button"
                      onClick={() => removeBulkAop(row.id)}
                      disabled={bulkSaving}
                      className="shrink-0 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      aria-label={`Remove ${row.displayName} from batch`}
                    >
                      <Close className="h-4 w-4" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        <section>
          <div className="mb-3 flex flex-row flex-wrap items-baseline gap-x-3 gap-y-1 sm:mb-4">
            <h2 className="shrink-0 text-lg font-semibold leading-snug text-gray-font md:text-xl">
              Practice Industries
            </h2>
            <button
              type="button"
              className="min-w-0 text-left text-base font-medium text-secondary hover:underline hover:text-secondary-dark"
              onClick={toggleAllBulkIndustries}
            >
              Select / Clear All Practice Industries
            </button>
          </div>
          <div className="staff-aop-mapping-checkboxes grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 md:gap-x-4 lg:gap-x-5">
            {bulkIndustryRows.map((ind) => (
              <CheckBox
                key={ind.id}
                className="min-w-0"
                label={ind.name}
                checked={Boolean(ind.isChecked)}
                onCheckedChange={(checked) => {
                  setBulkIndustryRows((prev) =>
                    prev.map((r) => (r.id === ind.id ? { ...r, isChecked: checked } : r)),
                  );
                }}
                labelClassName="text-base leading-snug"
                disabled={bulkSaving}
              />
            ))}
          </div>
        </section>
        </div>

        <div className="mt-4 flex shrink-0 flex-col border-t border-gray-100 bg-white pt-4 sm:mt-5 sm:pt-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-end sm:gap-3 pb-1.5 sm:pb-2">
            <Button
              className={BULK_MODAL_FOOTER_BUTTON_CLASS}
              text="Go back"
              onClick={closeMapModal}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={bulkSaving}
            />
            <Button
              className={BULK_MODAL_FOOTER_BUTTON_CLASS}
              text={bulkSaving ? "Saving…" : "Submit"}
              onClick={() => void applyBulkMapping()}
              color="secondary"
              variant="filled"
              size="fixed"
              icon={<SaveChanges />}
              disabled={!canSaveBulk}
            />
          </div>
        </div>
      </div>
    </Modal>
  );

  return (
    <>

      {mapModal}

      <FilteredTableCore
        key={tableRemountKey}
        columnsDef={columnsDef}
        showRowNumber
        selectionInsertAfterDataColumns={1}
        rowSelection={rowSelection}
        endpoint={AOP_MAPPING_ENDPOINT}
        mergePanelFiltersIntoPersistentFilter
        onQuickFilterChange={onQuickFilterChange}
        filtersData={filtersData}
        formatData={formatData}
        quickFilters={AOP_MAPPING_QUICK_FILTERS}
        quickFilterRequireSelection
        defaultPageSize={AOP_MAPPING_PAGE_SIZE}
        defaultSorting={AOP_MAPPING_DEFAULT_SORT}
        showSearchInput
        searchPlaceholder="Search by display name"
        excelFilename="aop-mapping"
        showExcelExport
        searchToolbarAccessory={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              text="Add AOP"
              color="secondary"
              variant="filled"
              size="fixed"
              onClick={() => navigate("/AOP/create")}
              icon={<Plus />}
            />
            <Button
              text={bulkOpenLoading ? "Loading…" : "Map Selection to Industry"}
              color="neutral"
              variant="outlined"
              size="fixed"
              disabled={mapButtonDisabled || bulkOpenLoading}
              onClick={() => void openBulkMappingModal()}
            />
          </div>
        }
      />
    </>
  );
};

export default AOPMapping;
